"use client"

type WindowWithDataLayer = Window & {
  dataLayer: Record<string, any>[]
}

declare const window: WindowWithDataLayer

export const pageView = (
  page_id: string,
  category?: string,
  user_id?: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "page_view",
      page_id,
      category,
      user_id,
    })
  }
}

// Core Events
export const vehicleCardViewed = (
  vehicle_id: string,
  category: string,
  price: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "vehicle_card_viewed",
      vehicle_id,
      category,
      price,
    })
  }
}

export const vehicleCardClicked = (
  vehicle_id: string,
  category: string,
  click_source: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "vehicle_card_clicked",
      vehicle_id,
      category,
      click_source,
    })
  }
}

export const imageGalleryInteraction = (
  vehicle_id: string,
  image_index: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "image_gallery_interaction",
      vehicle_id,
      image_index,
    })
  }
}

export const formStarted = (form_type: string, location_on_site: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "form_started",
      form_type,
      location_on_site,
    })
  }
}

export const formSubmitted = (form_type: string, fields_filled: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "form_submitted",
      form_type,
      fields_filled,
    })
  }
}

export const phoneClick = (phone_number: string, click_location: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "phone_click",
      phone_number,
      click_location,
    })
  }
}

export const whatsappClick = (
  whatsapp_number: string,
  click_location: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "whatsapp_click",
      whatsapp_number,
      click_location,
    })
  }
}

export const buttonClick = (
  button_id: string,
  button_text: string,
  page_section: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "button_click",
      button_id,
      button_text,
      page_section,
    })
  }
}

// Search Events
export const searchInitiated = (origin: string, timestamp: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "search_initiated",
      origin,
      timestamp,
    })
  }
}

export const headerSearchOpened = (origin_page: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "header_search_opened",
      origin_page,
    })
  }
}

export const headerSearchInput = (search_term: string, chars_count: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "header_search_input",
      search_term,
      chars_count,
    })
  }
}

export const headerSearchClosed = (origin_page: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "header_search_closed",
      origin_page,
    })
  }
}

export const filterApplied = (filter_type: string, selected_value: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "filter_applied",
      filter_type,
      selected_value,
    })
  }
}

export const sortSelected = (sort_type: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "sort_selected",
      sort_type,
    })
  }
}

export const vehicleCategorySelected = (category_name: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "vehicle_category_selected",
      category_name,
    })
  }
}

export const searchDealTypeSelected = (deal_type: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "search_deal_type_selected",
      deal_type,
    })
  }
}

export const monthlyBudgetRangeChanged = (
  min: string,
  max: string,
  selected_min: string,
  selected_max: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "monthly_budget_range_changed",
      min,
      max,
      selected_min,
      selected_max,
    })
  }
}

export const searchTagClicked = (tag_value: string, section: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "search_tag_clicked",
      tag_value,
      section,
    })
  }
}

export const popularModelClicked = (
  model_name: string,
  price: string,
  section: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "popular_model_clicked",
      model_name,
      price,
      section,
    })
  }
}

export const searchSubmitted = (
  deal_type: string,
  category_name: string,
  budget_min: string,
  budget_max: string,
  search_term: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "search_submitted",
      deal_type,
      category_name,
      budget_min,
      budget_max,
      search_term,
    })
  }
}

// Homepage Button / Banner Events
export const heroCtaClicked = (button_text: string, page_section: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "hero_cta_clicked",
      button_text,
      page_section,
    })
  }
}

export const promoBannerClicked = (
  campaign_name: string,
  banner_id: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "promo_banner_clicked",
      campaign_name,
      banner_id,
    })
  }
}

export const popularCategoryClicked = (
  category_name: string,
  section: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "popular_category_clicked",
      category_name,
      section,
    })
  }
}

export const viewAllVehiclesClicked = (section_name: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "view_all_vehicles_clicked",
      section_name,
    })
  }
}

export const whyUsIconClicked = (benefit_name: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "why_us_icon_clicked",
      benefit_name,
    })
  }
}

// Contact / Footer Events
export const contactUsFooterClicked = (link_text: string, section: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "contact_us_footer_clicked",
      link_text,
      section,
    })
  }
}

export const branchLocatorClicked = (
  location_type: string,
  section: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "branch_locator_clicked",
      location_type,
      section,
    })
  }
}

export const whatsappFloatingClicked = (location: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "whatsapp_floating_clicked",
      location,
    })
  }
}

// Advanced Interaction Events
export const vehicleComparisonAdded = (
  vehicle_id: string,
  category: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "vehicle_comparison_added",
      vehicle_id,
      category,
    })
  }
}

export const vehicleComparisonViewed = (compared_vehicle_ids: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "vehicle_comparison_viewed",
      compared_vehicle_ids,
    })
  }
}

export const vehicleFavorited = (vehicle_id: string, category: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "vehicle_favorited",
      vehicle_id,
      category,
    })
  }
}

export const calculatorUsed = (
  amount: string,
  interest_rate: string,
  period_months: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "calculator_used",
      amount,
      interest_rate,
      period_months,
    })
  }
}

export const tradeinFormSubmitted = (
  current_vehicle: string,
  desired_vehicle: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "tradein_form_submitted",
      current_vehicle,
      desired_vehicle,
    })
  }
}

export const ctaVehicleClicked = (vehicle_id: string, section_name: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "cta_vehicle_clicked",
      vehicle_id,
      section_name,
    })
  }
}

// Engagement Events
export const searchAbandoned = (
  filled_params: string,
  abandonment_step: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "search_abandoned",
      filled_params,
      abandonment_step,
    })
  }
}

export const formAbandoned = (
  form_type: string,
  filled_fields: string,
  abandonment_step: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "form_abandoned",
      form_type,
      filled_fields,
      abandonment_step,
    })
  }
}

export const exitIntentDetected = (
  current_page: string,
  time_spent_seconds: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "exit_intent_detected",
      current_page,
      time_spent_seconds,
    })
  }
}

export const videoPlayed = (video_id: string, location_on_page: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "video_played",
      video_id,
      location_on_page,
    })
  }
}

export const videoCompleted = (video_id: string, duration_seconds: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "video_completed",
      video_id,
      duration_seconds,
    })
  }
}

export const faqInteraction = (
  opened_question: string,
  location_on_page: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "faq_interaction",
      opened_question,
      location_on_page,
    })
  }
}

export const socialShareClicked = (
  platform: string,
  click_location: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "social_share_clicked",
      platform,
      click_location,
    })
  }
}

export const languageSelectionChanged = (
  selected_language: string,
  location_on_site: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "language_selection_changed",
      selected_language,
      location_on_site,
    })
  }
}

export const accessibilityToolUsed = (
  tool_used: string,
  location_on_site: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "accessibility_tool_used",
      tool_used,
      location_on_site,
    })
  }
}

// Behavioral / Diagnostic Events
export const sessionDuration = (
  total_duration_seconds: string,
  pages_viewed_count: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "session_duration",
      total_duration_seconds,
      pages_viewed_count,
    })
  }
}

export const pagesPerSession = (pages_count: string, page_ids: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "pages_per_session",
      pages_count,
      page_ids,
    })
  }
}

export const bounceRate = (page_id: string, traffic_source: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "bounce_rate",
      page_id,
      traffic_source,
    })
  }
}

export const conversionFunnelStep = (
  current_step: string,
  previous_steps: string,
  user_id: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "conversion_funnel_step",
      current_step,
      previous_steps,
      user_id,
    })
  }
}

export const scrollDepthReached = (
  scroll_percentage: string,
  page_id: string,
) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "scroll_depth_reached",
      scroll_percentage,
      page_id,
    })
  }
}

export const timeOnPage = (page_id: string, duration_seconds: string) => {
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "time_on_page",
      page_id,
      duration_seconds,
    })
  }
}
